/**
 * InterLogs Trade — Main Application Entrypoint (TypeScript)
 * Connects 24-hour market matrix, independent news window manager, and real-time SSE stream.
 */

import { MarketGridController } from './market_grid';
import { NewsWindowManager } from './news_window';
import { NewsStreamController } from './news_stream';
import { MarketSnapshot } from './types';

class InterlogsTradeApp {
  public grid: MarketGridController;
  public windowManager: NewsWindowManager;
  public stream: NewsStreamController;
  private eventSource: EventSource | null = null;

  constructor() {
    console.log('[InterLogs Trade] Initializing Suite (TypeScript Engine)...');

    this.grid = new MarketGridController();
    this.windowManager = new NewsWindowManager('news-feed-container');
    this.stream = new NewsStreamController(this.windowManager, 'news-feed-container');

    this.loadInitialMarketData();
    this.connectMarketStream();
  }

  private async loadInitialMarketData(): Promise<void> {
    try {
      const tzOffset = -Math.round(new Date().getTimezoneOffset() / 60);
      const res = await fetch(`/api/trade/snapshot?tz_offset=${tzOffset}`);
      if (res.ok) {
        const snap: MarketSnapshot = await res.json();
        this.grid.updateSnapshot(snap);
        this.grid.setConnectionStatus(true);
        if (!snap.matrix || (Array.isArray(snap.matrix) ? snap.matrix.length === 0 : Object.keys(snap.matrix).length === 0)) {
          const matrixRes = await fetch(`/api/trade/matrix?tz_offset=${tzOffset}`);
          if (matrixRes.ok) {
            const matrixData = await matrixRes.json();
            this.grid.renderMatrix(matrixData);
          }
        }
      }
    } catch (err) {
      console.warn('Initial market snapshot fetch failed:', err);
    }
  }

  private connectMarketStream(): void {
    try {
      if (this.eventSource) {
        this.eventSource.close();
      }

      this.eventSource = new EventSource('/api/trade/stream');

      this.eventSource.addEventListener('trade_update', (e: MessageEvent) => {
        try {
          const snap: MarketSnapshot = JSON.parse(e.data);
          this.grid.updateSnapshot(snap);
          this.grid.setConnectionStatus(true);
        } catch (err) {
          console.error('Trade update SSE parse error:', err);
        }
      });

      this.eventSource.onopen = () => {
        this.grid.setConnectionStatus(true);
      };

      this.eventSource.onerror = () => {
        this.grid.setConnectionStatus(false);
        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = null;
        }
        setTimeout(() => this.connectMarketStream(), 4000);
      };
    } catch (e) {
      console.warn('EventSource failed, using snapshot polling');
      setInterval(() => this.loadInitialMarketData(), 4000);
    }
  }
}

function initThemeToggle(): void {
  const button = document.getElementById('btn-theme-toggle') as HTMLButtonElement | null;
  if (!button) return;
  const icon = button.querySelector('.theme-icon');

  function applyTheme(theme: string): void {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('interlogs_trade_theme', theme);
      localStorage.setItem('interlogs_theme', theme);
    } catch (e) {}
    if (icon) {
      if (theme === 'light') {
        icon.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>`;
      } else {
        icon.innerHTML = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>`;
      }
    }
    if (button) {
      button.title = theme === 'light' ? 'Switch to Dark Theme' : 'Switch to Light Theme';
    }

    try {
      const app = (window as any).__TRADE_APP__;
      if (app && app.grid) {
        app.grid.syncCanvasDimensions();
        app.grid.renderAllStaticCanvases();
      }
    } catch (e) {}
  }

  const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark';
  applyTheme(currentTheme);

  button.addEventListener('click', () => {
    const isLight = document.documentElement.getAttribute('data-theme') === 'light';
    applyTheme(isLight ? 'dark' : 'light');
  });
}

function initSortBar(app: InterlogsTradeApp): void {
  const sortBar = document.getElementById('grid-sort-bar');
  if (!sortBar) return;
  const buttons = sortBar.querySelectorAll<HTMLButtonElement>('.sort-filter-btn');

  function updateActiveButtons(mode: string): void {
    buttons.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.sort === mode);
    });
  }

  // Set initial active button
  updateActiveButtons(app.grid.getSortMode());

  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.sort as any;
      if (mode && ['growth', 'fall', 'market_cap', 'name'].includes(mode)) {
        app.grid.setSortMode(mode);
        updateActiveButtons(mode);
      }
    });
  });
}

function bootstrap(): void {
  const app = new InterlogsTradeApp();
  (window as any).__TRADE_APP__ = app;
  initThemeToggle();
  initSortBar(app);
}

// Bootstrap once DOM content is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

